/**
 * Yvine Design — scenery "windows": the portrait discs (about page, home) and
 * the footer newsletter box show the page backdrop exactly as the fixed
 * .art-bg layer shows it behind the frosted frame, so a transparent portrait
 * looks cut out of the scenery and the scenery stays put while the page
 * scrolls over it.
 *
 * No per-scroll script (that always trails the compositor by a frame — the
 * "lag" — and iPhone's collapsing toolbar re-zoomed it). Instead a pane the
 * size of the whole scrollable page is placed inside the window, clipped by
 * the window's clip-path, and holds a position:sticky "sky" sized like .art-bg:
 * the browser keeps the sky pinned to the viewport while everything scrolls,
 * so the slice seen through the window is always the right one, on every
 * frame, with no JavaScript involved. Geometry is measured only on layout
 * changes (load, fonts, resize, size observers).
 *
 * The sky is made BLEED px taller than the backdrop at both ends (padding, see
 * .art-bgwin__sky): iOS Safari lets a sticky element trail the viewport while
 * its toolbar collapses or expands, whereas the fixed backdrop keeps up, and a
 * sky exactly one viewport tall then leaves a strip of frosted glass showing
 * through a window that sits at the screen's top edge. The bleed keeps scenery
 * under the window across that drift; the pane and the sticky offset are
 * shifted by the same amount so the aligned part does not move.
 *
 * Element: data-art="bg-window" — position:relative + clip-path, its visible
 * children above z-index 0. The CSS fixed-attachment background on it is only
 * the no-JS fallback and is switched off here.
 */
var BLEED = 120;

Art.section('bg-window', function (el) {
  // Built only when the window comes within a screen of view. At start-up the
  // page-sized sky is otherwise the largest thing Chrome sees and became the
  // page's LCP element, waiting on everything else; the footer's window is
  // rarely even scrolled to. Until then the CSS fallback stands in.
  if ('IntersectionObserver' in window) {
    var near = new IntersectionObserver(function (entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      near.disconnect();
      buildBgWindow(el);
    }, { rootMargin: '100% 0px' });
    near.observe(el);
  } else {
    buildBgWindow(el);
  }
});

function buildBgWindow(el) {
  var pane = document.createElement('div');
  pane.className = 'art-bgwin';
  pane.setAttribute('aria-hidden', 'true');
  var sky = document.createElement('div');
  sky.className = 'art-bgwin__sky';
  sky.style.setProperty('--art-sky-bleed', BLEED + 'px');
  pane.appendChild(sky);
  el.insertBefore(pane, el.firstChild);
  el.style.backgroundImage = 'none';

  var bg = document.querySelector('.art-bg');
  var ticking = false;

  function scroller() {
    var w = document.querySelector('.page-wrapper');
    return (w && /auto|scroll/.test(getComputedStyle(w).overflowY)) ? w : null; // desktop scrolls inside the frame
  }

  function place() {
    if (!el.isConnected) return;
    // Measure the page WITHOUT the pane: a page-sized pane would otherwise be
    // part of the measurement and grow the page on every pass.
    pane.style.display = 'none';
    var sc = scroller();
    var cs = getComputedStyle(el);
    var bl = parseFloat(cs.borderLeftWidth) || 0;
    var bt = parseFloat(cs.borderTopWidth) || 0;
    var r = el.getBoundingClientRect();
    var portTop, portLeft, originX, originY, contentW, contentH;
    if (sc) {
      var sr = sc.getBoundingClientRect();
      portTop = sr.top + sc.clientTop;
      portLeft = sr.left + sc.clientLeft;
      originX = r.left + bl - portLeft + sc.scrollLeft;
      originY = r.top + bt - portTop + sc.scrollTop;
      contentW = sc.scrollWidth;
      contentH = sc.scrollHeight;
    } else {
      portTop = 0;
      portLeft = 0;
      originX = r.left + bl + window.scrollX;
      originY = r.top + bt + window.scrollY;
      contentW = document.documentElement.scrollWidth;
      contentH = document.documentElement.scrollHeight;
    }
    var b = bg ? bg.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    pane.style.display = '';
    // The pane covers the whole scrollable page, starting one bleed above the
    // backdrop's top (on desktop that is the frame margin above the
    // scrollport, so the sky's resting position already matches the layer
    // before any scrolling), expressed from the window's padding-box origin.
    var lift = portTop - b.top;
    pane.style.left = (-originX) + 'px';
    pane.style.top = (-originY - lift - BLEED) + 'px';
    pane.style.width = contentW + 'px';
    pane.style.height = (contentH + b.height + lift + 2 * BLEED) + 'px';
    // The sky's content box is the backdrop layer's box, pinned where that
    // layer sits relative to the scrollport (0 on phones, minus the frame on
    // desktop), less the bleed that pads it above.
    sky.style.width = b.width + 'px';
    sky.style.height = b.height + 'px';
    sky.style.top = (b.top - portTop - BLEED) + 'px';
    sky.style.marginLeft = (b.left - portLeft) + 'px';
  }

  function tick() {
    if (ticking) return;
    // Keyboard opening/closing (Art.keyboardResize, art-core.js): not a real
    // layout change, and re-placing the sky mid-animation is the flick.
    if (window.Art && Art.keyboardResize && Art.keyboardResize()) return;
    ticking = true;
    requestAnimationFrame(function () { ticking = false; place(); });
  }

  place();
  window.addEventListener('resize', tick, { passive: true });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(tick);
  // Late layout changes (fonts, images, the header reserving its height on
  // load) move the window: re-place when anything above it resizes.
  function settle() { tick(); setTimeout(tick, 250); setTimeout(tick, 900); setTimeout(tick, 2000); }
  if (document.readyState === 'complete') settle(); else window.addEventListener('load', settle);
  if ('ResizeObserver' in window) {
    var ro = new ResizeObserver(tick);
    [document.documentElement, document.body, document.getElementById('MainContent'), document.getElementById('header-group'), el.closest('section, .shopify-section'), el.parentElement]
      .forEach(function (n) { if (n) ro.observe(n); });
  }
  document.addEventListener('art:page:swap', tick);
}
